import './Edit.css';
import React, { useEffect, useState } from 'react';
import { useConfig } from "./ConfigContext";
import { useNavigate } from "react-router-dom";
import GetAccessToken from "./Tokens";
import axios from "axios";
import editIcon from "./icons/edit-2.png";
import TagInput from "./TagInput";

const Edit = () => {
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [specimens, setSpecimens] = useState([]);
    const [loadingSpecimens, setLoadingSpecimens] = useState(false);
    const [error, setError] = useState('');
    const config = useConfig();
    const navigate = useNavigate();

    const groupServiceUrl = config?.group_service_url;
    const specimenServiceUrl = config?.model_service_url;

    const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
    const [editingSpecimen, setEditingSpecimen] = useState(null);
    const [editName, setEditName] = useState('');
    const [preEditGroup, setPreEditGroup] = useState(null);
    const [editGroup, setEditGroup] = useState(null);
    const [preEditTags, setPreEditTags] = useState([]);
    const [editTags, setEditTags] = useState([]);
    const ref = React.useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsEditPanelOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
    }, []);


    const fetchSpecimenTags = async (specimenId) => {
        const access_token = await GetAccessToken(config.user_service_url);
        if (!access_token) return navigate('/login');

        try {
            const response = await fetch(`${specimenServiceUrl}/private/specimenTags?specimen_id=${specimenId}`, {
                headers: {
                    'Authorization': `Bearer ${access_token}`
                }
            });

            if (response.status === 401) {
                sessionStorage.removeItem('access_token');
                return fetchSpecimenTags(specimenId);
            }

            const data = await response.json();
            return data.tags;
        } catch (err) {
            console.error('Error fetching specimen tags:', err);
            return [];
        }
    };

    useEffect(() => {
        const fetchGroups = async () => {
            const access_token = await GetAccessToken(config.user_service_url);
            if (!access_token) return navigate('/login');
            try {
                const response = await fetch(`${groupServiceUrl}/public/list?include_description=true`, {
                    headers: { 'Authorization': `Bearer ${access_token}` }
                });
                if (response.status === 401) {
                    sessionStorage.removeItem('access_token');
                    return fetchGroups(); // retry
                }
                const data = await response.json();
                setGroups(data.groups);
            } catch (err) {
                setError('Error fetching groups.');
            }
        };

        if (groupServiceUrl && config) {
            fetchGroups();
        }
    }, [groupServiceUrl, config, navigate]);

    useEffect(() => {
        const fetchSpecimens = async () => {
            if (!selectedGroup) return;
            setLoadingSpecimens(true);
            const access_token = await GetAccessToken(config.user_service_url);
            if (!access_token) return navigate('/login');
            try {
                const response = await fetch(`${groupServiceUrl}/public/specimens?group_id=${selectedGroup.group_id}`, {
                    headers: { 'Authorization': `Bearer ${access_token}` }
                });
                if (response.status === 401) {
                    sessionStorage.removeItem('access_token');
                    return fetchSpecimens(); // retry
                }
                const data = await response.json();
                setSpecimens(data.specimens);
            } catch (err) {
                setError('Error fetching specimens.');
            }
            setLoadingSpecimens(false);
        };

        if (selectedGroup && specimenServiceUrl && config) {
            fetchSpecimens();
        }
    }, [selectedGroup, specimenServiceUrl, config, navigate]);

    const handleGroupChange = (e) => {
        const groupId = e.target.value;
        const group = groups.find(g => g.group_id === parseInt(groupId));
        setSelectedGroup(group);
        setSpecimens([]);
    };

    const handleEditSpecimen = async (specimen) => {
        setEditingSpecimen(specimen);
        setEditName(specimen.specimen_name);
        setEditGroup(selectedGroup);
        setPreEditGroup(selectedGroup);
        const tags = await fetchSpecimenTags(specimen.specimen_id);
        setEditTags(tags || []);
        setPreEditTags(tags || []);
        setIsEditPanelOpen(true);
    };

    const handleSaveEdit = async () => {
        const access_token = await GetAccessToken(config.user_service_url);
        const preEditSet = new Set(preEditTags);
        const editSet = new Set(editTags);
        const addedTags = editTags.filter(tag => !preEditSet.has(tag));
        const removedTags = preEditTags.filter(tag => !editSet.has(tag));
        let nameParam = null;
        let groupParam = null;
        if (editingSpecimen.specimen_name !== editName)
            nameParam = editName;
        if (preEditGroup.group_id !== editGroup.group_id)
            groupParam = editGroup.group_id;
        if (!access_token) return navigate('/login');
        try {
            const response = await axios.put(
                `${specimenServiceUrl}/private/updateSpecimen`,
                {
                    specimen_id: editingSpecimen.specimen_id,
                    specimen_name: nameParam,
                    group_id: groupParam,
                    addedTags: addedTags,
                    removedTags: removedTags
                },
                {
                    headers: {
                        'Authorization': `Bearer ${access_token}`
                    }
                }
            );

            if (response.status === 200) {
                let updated;
                if (groupParam !== null) {
                    updated = specimens.filter(spec => spec.specimen_id !== editingSpecimen.specimen_id);
                } else {
                    updated = specimens.map(spec =>
                        spec.specimen_id === editingSpecimen.specimen_id
                            ? { ...spec, specimen_name: editName, tags: editTags }
                            : spec
                    );
                }
                setSpecimens(updated);
                setIsEditPanelOpen(false);
            }
        } catch (err) {
            setError("Error updating specimen.");
        }
    };

    return (
        <div className="edit-section">
            {(<div>
                <h2>Edit Specimens</h2>
                {error && <div style={{ color: 'red' }}>{error}</div>}
                <div className="group-dropdown">
                    <label htmlFor="groupSelect">Select Group:</label>
                    <select
                        id="groupSelect"
                        onChange={handleGroupChange}
                        value={selectedGroup ? selectedGroup.group_id : ''}>
                        {!selectedGroup && <option value="">-- Select a group --</option>}
                        {groups.map(group => (
                            <option key={group.group_id} value={group.group_id}>
                                {group.group_name}
                            </option>
                        ))}
                    </select>
                </div>
                {selectedGroup && (
                    <div className="specimen-list">
                        <h3>Specimens in {selectedGroup.group_name}</h3>
                        {loadingSpecimens ? (
                            <p>Loading specimens...</p>
                        ) : specimens.length === 0 ? (
                            <p>No specimens found in this group.</p>
                        ) : (
                            <ul>
                                {specimens.map(specimen => (
                                    <li key={specimen.specimen_id} style={{ marginBottom: '10px' }}>
                                        <span>{specimen.specimen_name}</span>
                                        <button
                                            style={{ marginLeft: '20px', backgroundColor: 'transparent' }}
                                            onClick={() => handleEditSpecimen(specimen)}>
                                            <img src={editIcon} alt="edit" style={{ width: '32px', height: '32px' }} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>)
            }

            {isEditPanelOpen && (
                <div className="overlay" ref={ref}>
                    <div className="edit-panel">
                        <h3>Edit Specimen</h3>
                        <label>Specimen Name:</label>
                        <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                        />

                        <label style={{ marginTop: '10px' }}>Group:</label>
                        <select
                            value={editGroup?.group_id}
                            onChange={(e) => {
                                const newGroup = groups.find(g => g.group_id === parseInt(e.target.value));
                                setEditGroup(newGroup);
                            }}
                        >
                            {groups.map(group => (
                                <option key={group.group_id} value={group.group_id}>
                                    {group.group_name}
                                </option>
                            ))}
                        </select>

                        <label style={{ marginTop: '10px' }}>Tags:</label>
                        <TagInput tags={editTags} setTags={setEditTags} />

                        <div style={{ marginTop: '15px' }}>
                            <button onClick={handleSaveEdit}>Save</button>
                            <button onClick={() => setIsEditPanelOpen(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Edit;
